'use client';

import {
  ALL_MEMBERSHIP_TIERS,
  CompareTableSubcopy,
  MembershipTier,
  MembershipTierComparisonFeatureList,
  MembershipTierWithTrial,
  TRIAL,
} from '@/lib/constants/maverick';
import { useEffect, useState } from 'react';
import { useSiteStore } from '@/store/SiteStoreProvider';
import { useTranslation } from '@whoop/i18n/lang/client';
import TierFeatureListHeader from '@/components/OPLComponents/MembershipComparison/TierFeatureListHeader/TierFeatureListHeader';
import { MembershipTierInfo } from '@/types/maverickTypes';
import { Product } from 'services/generated/commerce-service/models/Product';
import TierFeatureLists from './TierFeatureLists/TierFeatureLists';
import { useShallow } from 'zustand/react/shallow';
import { moveToComparisonTable } from '@/lib/utils/maverickHelpers';
import SectionHeader from '@/components/SectionHeader/SectionHeader';
import { getFormattedTierPrices } from './membershipComparisonUtils';
import { Trans } from '@whoop/i18n/lang/client';
import { UnstyledButton } from 'ui';

interface MembershipComparisonProps {
  availableTiers: MembershipTierInfo[];
  softSelectedTier: MembershipTier;
  onSelect: (
    tierName: MembershipTierWithTrial,
    product?: Product,
  ) => Promise<void>;
  trialMembership?: Product;
  membershipTierFeatureLists: MembershipTierComparisonFeatureList[];
  isUpgrade?: boolean;
  isAddingToCart: boolean;
  compareTableSubcopyData?: CompareTableSubcopy[];
  onShowDisclaimers: () => void;
}

// Tiers to display on dropdowns based on soft selected tier
const tierPairs = {
  [MembershipTier.ONE]: [MembershipTier.ONE, MembershipTier.LIFE],
  [MembershipTier.PEAK]: [MembershipTier.PEAK, MembershipTier.LIFE],
  [MembershipTier.LIFE]: [MembershipTier.PEAK, MembershipTier.LIFE],
};

export default function MembershipComparison({
  availableTiers,
  softSelectedTier,
  onSelect,
  trialMembership,
  membershipTierFeatureLists,
  isUpgrade,
  isAddingToCart,
  compareTableSubcopyData,
  onShowDisclaimers,
}: MembershipComparisonProps): React.JSX.Element {
  const { t } = useTranslation('membershipTiers');
  const availableTierNames: MembershipTierWithTrial[] = availableTiers
    .map((tier) => tier.name)
    .sort(
      (a, b) =>
        ALL_MEMBERSHIP_TIERS.indexOf(a) - ALL_MEMBERSHIP_TIERS.indexOf(b),
    );
  const showTrialInfo = Boolean(trialMembership);
  if (showTrialInfo) {
    availableTierNames.push(TRIAL);
  }
  const [firstSelectedTier, setFirstSelectedTier] =
    useState<MembershipTierWithTrial>(availableTierNames[0]);
  const [secondSelectedTier, setSecondSelectedTier] =
    useState<MembershipTierWithTrial>(availableTierNames[1]);

  const { currency, language, region } = useSiteStore(
    useShallow((state) => ({
      currency: state.currency,
      language: state.language,
      region: state.region,
    })),
  );
  const formattedTierPrices = getFormattedTierPrices(
    availableTiers,
    currency,
    language,
    region,
  );

  const handleDropdownSelect = (
    tierName: MembershipTierWithTrial,
    index: number,
  ) => {
    if (index === 0) {
      setFirstSelectedTier(tierName);
    } else {
      setSecondSelectedTier(tierName);
    }
    moveToComparisonTable();
  };

  // Update selected tiers when softSelectedTier changes
  useEffect(() => {
    const [preferredFirstTier, preferredSecondTier] =
      tierPairs[softSelectedTier] || [];

    if (
      availableTierNames.includes(preferredFirstTier) &&
      availableTierNames.includes(preferredSecondTier)
    ) {
      setFirstSelectedTier(preferredFirstTier);
      setSecondSelectedTier(preferredSecondTier);
    } else {
      setFirstSelectedTier(availableTierNames[0]);
      setSecondSelectedTier(availableTierNames[1]);
    }
  }, [softSelectedTier]);

  const handleTierSelect = async (tierName: MembershipTierWithTrial) => {
    if (tierName === TRIAL) {
      await onSelect(MembershipTier.PEAK, trialMembership);
    } else {
      await onSelect(tierName);
    }
  };

  return (
    <div
      className='flex w-full justify-center pb-8 pt-14'
      id='membership-comparison-table'
      data-testid='membership-comparison-table'
    >
      <div className='flex w-[88%] max-w-screen-lg flex-col'>
        <SectionHeader className='pb-4 md:pb-8'>
          {isUpgrade ? t('compareTheLineup') : t('compareTheMemberships')}
        </SectionHeader>
        {/* TODO : implement and open chat bubble here (only on desktop) - ECOMM-2067 */}
        <div className='relative flex h-full w-full flex-col items-center justify-center'>
          <TierFeatureListHeader
            tiers={availableTierNames}
            selectedTiers={[firstSelectedTier, secondSelectedTier]}
            handleDropdownSelect={handleDropdownSelect}
          />
          <TierFeatureLists
            tiers={availableTierNames}
            selectedTiers={[firstSelectedTier, secondSelectedTier]}
            membershipTierFeatureLists={membershipTierFeatureLists}
            onSelect={handleTierSelect}
            isAddingToCart={isAddingToCart}
            formattedTierPrices={formattedTierPrices}
            softSelectedTier={softSelectedTier}
            compareTableSubcopyData={compareTableSubcopyData}
            isUpgrade={isUpgrade}
          />
        </div>
        <div className='pt-4 text-center md:pt-14'>
          <Trans
            i18nKey='membershipTiers:additionalMedicalInformationBelow'
            components={[
              <UnstyledButton
                data-testid='show-disclaimers-button'
                className='underline'
                onClick={onShowDisclaimers}
              />,
            ]}
          />
        </div>
      </div>
    </div>
  );
}
